import { useEffect, useState } from 'react'
import { Head, router } from '@inertiajs/react'
import AppLayout from '@/components/AppLayout.tsx'
import PlanHeader from '../components/PlanHeader'
import PlanBreakdownSections, { type ChannelSectionData, type QuarterlySectionData } from './components/PlanBreakdownSections'
import RowCommentThread from './components/RowCommentThread'
import OrphanCommentsBlock from './components/OrphanCommentsBlock'
import RevisionRequestWidget from './components/RevisionRequestWidget'
import { type PlanNodeFromServer } from './components/VersionViewOverallSection'
import { anchorTypeFor, parseRowParam, rowKey, type RowAnchor } from './components/rowAnchor'
import { type RequestPage } from './components/revisionRequest'
import { useRequestComments } from './components/useRequestComments'
import {
  plan_top_down_revision_request_new_path,
  plan_top_down_revision_request_comment_path,
  plan_top_down_revision_request_comments_path,
  plan_top_down_revision_request_path,
} from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'

interface Props {
  slug: string
  begin_date: string
  end_date: string
  top_down_plan: { id: string; name: string; slug: string; revenue_target: number; currency: string | null; org_node: { id: string; name: string; slug: string } | null }
  plan_nodes: PlanNodeFromServer[]
  channel_section: ChannelSectionData
  quarterly_section: QuarterlySectionData
  request: RequestPage | null
  open_row: string | null
  // Set when another planning task opened on the plan meanwhile: the
  // requester can still read and discard, but not comment or send.
  blocked_reason: string | null
}

// The manager's commenting page, GitHub's "start a review": the published
// plan's tables with a + on every row, comments inline under their rows,
// and the header widget that leads to the task form once there is at least
// one comment. `?row=<section>:<id>` opens the composer on that row.
export default function RevisionRequest({ slug, begin_date, end_date, top_down_plan, plan_nodes, channel_section, quarterly_section, request, open_row, blocked_reason }: Props) {
  const market = useMarket()
  const [composerKey, setComposerKey] = useState<string | null>(() => {
    if (blocked_reason) return null
    const parsed = parseRowParam(open_row)
    return parsed ? rowKey(parsed) : null
  })
  const comments = request?.comments ?? []
  const { byRow, orphans } = useRequestComments(comments)

  useEffect(() => {
    if (!composerKey) return
    document.querySelector(`[data-row-thread="${composerKey}"]`)?.scrollIntoView({ block: 'center' })
  }, [composerKey])

  const commentsPath = plan_top_down_revision_request_comments_path(market.slug, slug)
  const post = (anchor: RowAnchor, body: string) => {
    router.post(commentsPath, { section: anchor.section, anchor_type: anchorTypeFor(anchor.section), anchor_id: anchor.anchor_id, body }, {
      preserveScroll: true, onSuccess: () => setComposerKey(null),
    })
  }
  const update = (id: string, body: string) =>
    router.patch(plan_top_down_revision_request_comment_path(market.slug, slug, id), { body }, { preserveScroll: true })
  const remove = (id: string) =>
    router.delete(plan_top_down_revision_request_comment_path(market.slug, slug, id), { preserveScroll: true })
  const discard = () => {
    if (window.confirm('Discard all your comments on this plan?')) router.delete(plan_top_down_revision_request_path(market.slug, slug))
  }

  return (
    <AppLayout
      header={<PlanHeader name={top_down_plan.name} orgNodeName={top_down_plan.org_node?.name ?? null} revenueTarget={top_down_plan.revenue_target} currency={top_down_plan.currency} beginDate={begin_date} endDate={end_date} />}
      headerActions={<RevisionRequestWidget count={comments.length} formHref={plan_top_down_revision_request_new_path(market.slug, slug)} onDiscard={discard} sendDisabled={!!blocked_reason} />}
    >
      <Head title={`${top_down_plan.name} — Request revision`} />
      <div className="mb-4 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5 text-sm text-foreground">
        Commenting on the published plan. Click <span className="font-semibold">+</span> on a row to add a comment. When you are done, hit Request revision at the top.
      </div>
      {blocked_reason && (
        <div className="mb-4 rounded-lg border border-warning/30 bg-warning-surface px-4 py-2.5 text-sm text-foreground">
          {blocked_reason} You can keep your comments for later or discard them.
        </div>
      )}
      <PlanBreakdownSections
        planNodes={plan_nodes}
        channelSection={channel_section}
        quarterlySection={quarterly_section}
        currency={top_down_plan.currency}
        onRowAction={blocked_reason ? undefined : (anchor) => setComposerKey(rowKey(anchor))}
        rowExtras={(anchor) => {
          const key = rowKey(anchor)
          const list = byRow.get(key) ?? []
          if (list.length === 0 && composerKey !== key) return null
          return (
            <RowCommentThread
              anchor={anchor}
              comments={list}
              composerOpen={composerKey === key}
              readOnly={!!blocked_reason}
              onOpenComposer={() => setComposerKey(key)}
              onCloseComposer={() => setComposerKey(null)}
              onCreate={(body) => post(anchor, body)}
              onUpdate={update}
              onDelete={remove}
            />
          )
        }}
      />
      <OrphanCommentsBlock comments={orphans} readOnly={Boolean(blocked_reason)} onDelete={remove} />
    </AppLayout>
  )
}
